import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Check, Circle, Plus, Calendar, TrendingUp } from "lucide-react";
import { useTranslation } from "../translations";

export function TodaysHabits({ user }) {
  const { t, currentLanguage } = useTranslation();
  const [activities, setActivities] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [completedToday, setCompletedToday] = useState({});
  
  // Universal activities for all rubber tree producers - use translations
  const getUniversalActivities = () => [
    {
      id: 1,
      name: t('bleeding'),
      category: t('production'),
      type: "daily",
      description: t('bleedingDesc'),
      icon: "image",
      imageUrl: "https://tropicalestufas.com.br/wp-content/uploads/2024/02/abrir-materia-creditos-shutterstock.jpg"
    },
    {
      id: 2,
      name: t('productionCollection'),
      category: t('production'), 
      type: "periodic",
      description: t('productionCollectionDesc'),
      icon: "image",
      imageUrl: "https://customer-assets.emergentagent.com/job_talent-track-3/artifacts/zovpnr3i_Imagem%20do%20WhatsApp%20de%202025-09-19%20%C3%A0%28s%29%2016.56.57_2eb1b65f.jpg"
    },
    {
      id: 3,
      name: t('generalApplications'),
      category: t('management'),
      type: "sporadic",
      description: t('generalApplicationsDesc'),
      icon: "image",
      imageUrl: "https://customer-assets.emergentagent.com/job_talent-track-3/artifacts/f0g0aluc_Imagem%20do%20WhatsApp%20de%202025-09-19%20%C3%A0%28s%29%2017.40.31_237d3040.jpg"
    }
  ];

  useEffect(() => {
    // Load activities and restore completion state from localStorage
    const loadActivities = () => {
      setIsLoading(true);
      
      // Get today's date as key
      const today = new Date().toISOString().split('T')[0];
      const storageKey = `hevea-activities-${today}`;
      
      // Load completion state from localStorage
      const savedState = localStorage.getItem(storageKey);
      if (savedState) {
        try {
          setCompletedToday(JSON.parse(savedState));
        } catch (error) {
          console.log('Error loading saved activities state');
        }
      }
      
      setTimeout(() => {
        setActivities(getUniversalActivities());
        setIsLoading(false);
      }, 500); // Reduced loading time
    };

    loadActivities();
  }, [t]);

  const handleToggleCompletion = (activityId) => {
    const newState = {
      ...completedToday,
      [activityId]: !completedToday[activityId]
    };
    
    setCompletedToday(newState);
    
    // Save to localStorage with today's date as key
    const today = new Date().toISOString().split('T')[0];
    const storageKey = `hevea-activities-${today}`;
    localStorage.setItem(storageKey, JSON.stringify(newState));
  };

  const formatCompletionTime = () => {
    return new Date().toLocaleTimeString('pt-BR', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getLocale = () => {
    switch(currentLanguage) {
      case 'en': return 'en-US';
      case 'es': return 'es-ES';
      default: return 'pt-BR';
    }
  };

  const today = new Date().toLocaleDateString(getLocale(), {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="h-7 bg-muted rounded animate-pulse w-48" />
            <div className="h-5 bg-muted rounded animate-pulse w-32" />
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="flex items-center justify-between p-4 border rounded-lg">
                <div className="flex items-center space-x-4">
                  <div className="w-10 h-10 rounded-full bg-muted animate-pulse" />
                  <div className="space-y-2">
                    <div className="h-5 bg-muted rounded animate-pulse w-48" />
                    <div className="h-4 bg-muted rounded animate-pulse w-32" />
                  </div>
                </div>
                <div className="text-right space-y-2">
                  <div className="h-4 bg-muted rounded animate-pulse w-20" />
                  <div className="h-3 bg-muted rounded animate-pulse w-16" />
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="overflow-hidden">
      <CardHeader className="px-4 py-4 sm:px-6 sm:py-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1">
          <div className="flex items-center space-x-2">
            <Calendar className="h-5 w-5 sm:h-6 sm:w-6 text-green-600 shrink-0" />
            <CardTitle className="text-lg sm:text-xl">{t('todaysActivities')}</CardTitle>
          </div>
          <span className="text-xs sm:text-sm text-muted-foreground pl-7 sm:pl-0">
            {today}
          </span>
        </div>
        <CardDescription className="text-xs sm:text-sm pl-7 sm:pl-0">
          {t('manageActivitiesDesc')}
        </CardDescription>
      </CardHeader>
      
      <CardContent className="px-4 sm:px-6">
        <div className="space-y-3 sm:space-y-4">
          {activities.map((activity) => {
            const isCompleted = completedToday[activity.id];
            const isDailyActivity = activity.type === 'daily';
            
            return (
              <div 
                key={activity.id} 
                className="p-3 sm:p-4 border rounded-lg hover:shadow-md transition-shadow"
              >
                {/* Top row: checkbox + image + name + status (desktop) */}
                <div className="flex items-center gap-2 sm:gap-3">
                  {/* Completion button */}
                  <Button
                    variant="ghost"
                    size="icon"
                    className={`w-7 h-7 sm:w-10 sm:h-10 rounded-full shrink-0 ${
                      isCompleted
                        ? "bg-green-600 text-white"
                        : isDailyActivity 
                        ? "border-2 border-muted-foreground text-muted-foreground hover:border-green-600 hover:text-green-600"
                        : "border-2 border-blue-300 text-blue-500 hover:border-blue-600 hover:text-blue-600"
                    }`}
                    onClick={() => handleToggleCompletion(activity.id)}
                  >
                    {isCompleted ? (
                      <Check className="h-3.5 w-3.5 sm:h-5 sm:w-5" />
                    ) : (
                      <Circle className="h-2.5 w-2.5 sm:h-3 sm:w-3" />
                    )}
                  </Button>

                  {/* Activity image */}
                  <div className="w-7 h-7 sm:w-10 sm:h-10 rounded-full overflow-hidden shrink-0">
                    {activity.icon === "image" ? (
                      <img 
                        src={activity.imageUrl} 
                        alt={activity.name}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-lg sm:text-2xl">{activity.icon}</div>
                    )}
                    {activity.icon === "image" && (
                      <div className="w-full h-full items-center justify-center text-lg sm:text-2xl" style={{display: 'none'}}>
                        {activity.name === t('bleeding') ? "🔪" : 
                         activity.name === t('productionCollection') ? "⚖️" : "🚜"}
                      </div>
                    )}
                  </div>
                  
                  {/* Name */}
                  <h4 className="font-medium text-sm sm:text-base text-card-foreground flex-1 min-w-0 leading-tight">
                    {activity.name}
                  </h4>

                  {/* Status - right side */}
                  <div className="text-right shrink-0 ml-auto">
                    <div className={`text-[11px] sm:text-sm leading-tight ${
                      isCompleted 
                        ? "text-green-600 font-medium" 
                        : isDailyActivity
                        ? "text-muted-foreground"
                        : "text-blue-600"
                    }`}>
                      {isCompleted 
                        ? t('completed') 
                        : isDailyActivity 
                        ? t('pending') 
                        : t('notNeededToday')
                      }
                    </div>
                    <div className="text-[10px] sm:text-xs text-muted-foreground leading-tight">
                      {isCompleted 
                        ? formatCompletionTime()
                        : isDailyActivity
                        ? t('dailyGoal')
                        : t('whenNeeded')
                      }
                    </div>
                  </div>
                </div>

                {/* Bottom row: badges + description */}
                <div className="ml-[60px] sm:ml-[84px] mt-1.5">
                  <div className="flex flex-wrap items-center gap-1 sm:gap-2">
                    <Badge variant="outline" className="text-[10px] sm:text-xs px-1.5 sm:px-2 h-5">
                      {activity.category}
                    </Badge>
                    <Badge 
                      variant={isDailyActivity ? "default" : "secondary"} 
                      className="text-[10px] sm:text-xs px-1.5 sm:px-2 h-5"
                    >
                      {activity.type === 'daily' ? t('daily') : 
                       activity.type === 'periodic' ? t('periodic') : t('sporadic')}
                    </Badge>
                  </div>
                  <p className="text-[11px] sm:text-xs text-muted-foreground mt-1 line-clamp-2">
                    {activity.description}
                  </p>
                </div>
              </div>
            );
          })}

          {activities.length === 0 && (
            <div className="text-center py-8">
              <Calendar className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">
                {t('noActivitiesYet')}
              </p>
              <Button className="mt-4" size="sm">
                <Plus className="h-4 w-4 mr-2" />
                {t('addActivity')}
              </Button>
            </div>
          )}
          
          {activities.length > 0 && (
            <div className="mt-6 p-4 bg-muted/50 rounded-lg">
              <div className="flex items-center justify-between text-sm">
                <span className="font-medium">{t('dayProgress')}</span>
                <div className="flex items-center space-x-2">
                  <TrendingUp className="h-4 w-4 text-green-600" />
                  <span className="text-green-600 font-medium">
                    {(() => {
                      const dailyActivities = activities.filter(a => a.type === 'daily');
                      const completedDaily = dailyActivities.filter(a => completedToday[a.id]);
                      const completedOthers = activities.filter(a => a.type !== 'daily' && completedToday[a.id]);
                      
                      return `${completedDaily.length} ${t('of')} ${dailyActivities.length} ${t('essentials')}${
                        completedOthers.length > 0 ? ` + ${completedOthers.length} ${completedOthers.length > 1 ? t('extras') : t('extra')}` : ''
                      }`;
                    })()}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}